import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import {
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  CalendarClock,
  ExternalLink,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const providerLabel = (p) =>
  p === "stripe" ? "Card (Stripe)" : p === "paypal" ? "PayPal" : "—";

export default function ManageSubscription() {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/subscription/details");
      setDetails(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPortal = async () => {
    setAction("portal");
    try {
      const { data } = await api.post("/payments/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAction("");
    }
  };

  const cancel = async () => {
    setAction("cancel");
    try {
      const { data } = await api.post("/subscription/cancel", {});
      toast.success(data.message || "Subscription cancelled.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAction("");
    }
  };

  const reactivate = async () => {
    setAction("reactivate");
    try {
      const { data } = await api.post("/subscription/reactivate", {});
      toast.success(data.message || "Subscription reactivated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAction("");
    }
  };

  const active = details?.active;
  const isAdmin = details?.is_admin;
  const provider = details?.provider;
  const isStripe = provider === "stripe";
  const isPaypal = provider === "paypal";
  const willCancel = details?.cancel_at_period_end;
  const history = details?.billing_history || [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 py-10" data-testid="manage-subscription-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="manage-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-3xl bg-[#2D3748] flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-head font-extrabold text-3xl leading-none">Manage subscription</h1>
            <p className="text-muted-foreground text-sm mt-1">Your plan, billing and payments</p>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-black/5 animate-pulse h-52" data-testid="manage-loading" />
        ) : isAdmin ? (
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-black/5" data-testid="manage-admin-card">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-[#48BB78]" />
              <div>
                <p className="font-head font-bold text-lg">Admin access</p>
                <p className="text-sm text-muted-foreground">You have full access — no subscription needed.</p>
              </div>
            </div>
          </div>
        ) : !active ? (
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-black/5 text-center" data-testid="manage-inactive-card">
            <div className="w-14 h-14 rounded-3xl bg-[#E8A87C] flex items-center justify-center mx-auto">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-head font-extrabold text-2xl mt-5">No active subscription</h2>
            <p className="text-muted-foreground mt-2">
              Subscribe for $3/month to unlock every program and feature.
            </p>
            <button
              onClick={() => navigate("/subscribe")}
              className="mt-6 w-full bg-[#2D3748] text-white font-semibold py-4 rounded-full hover:-translate-y-0.5 transition-transform"
              data-testid="manage-subscribe-cta"
            >
              Subscribe now
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-3xl p-7 shadow-sm border border-black/5" data-testid="manage-status-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  {willCancel ? (
                    <AlertCircle className="w-6 h-6 text-[#E8A87C]" />
                  ) : (
                    <CheckCircle2 className="w-6 h-6 text-[#48BB78]" />
                  )}
                  <span
                    className="font-head font-extrabold text-xl"
                    data-testid="manage-status-label"
                  >
                    {willCancel ? "Ending soon" : "Active"}
                  </span>
                </div>
                <span className="text-2xl font-head font-extrabold">
                  $3<span className="text-sm text-muted-foreground font-semibold">/mo</span>
                </span>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-[#FBF9F6] p-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Payment method</p>
                  <p className="font-semibold mt-1" data-testid="manage-provider">{providerLabel(provider)}</p>
                </div>
                <div className="rounded-2xl bg-[#FBF9F6] p-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5" /> {willCancel ? "Access until" : "Renews on"}
                  </p>
                  <p className="font-semibold mt-1" data-testid="manage-renewal-date">
                    {fmtDate(details?.current_period_end)}
                  </p>
                </div>
              </div>

              {willCancel && (
                <div className="mt-4 rounded-2xl bg-[#FDF0E6] border border-[#E8A87C]/30 p-4" data-testid="manage-cancel-notice">
                  <p className="text-sm text-[#7A4B2B]">
                    Your subscription is set to end. You'll keep full access until{" "}
                    <strong>{fmtDate(details?.current_period_end)}</strong>.
                  </p>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {isStripe && (
                  <button
                    onClick={openPortal}
                    disabled={!!action}
                    className="w-full bg-[#2D3748] text-white font-semibold py-3.5 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="manage-portal-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {action === "portal" ? "Opening…" : "Manage billing & payment method"}
                  </button>
                )}

                {willCancel && isStripe ? (
                  <button
                    onClick={reactivate}
                    disabled={!!action}
                    className="w-full bg-[#48BB78] text-white font-semibold py-3.5 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="manage-reactivate-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {action === "reactivate" ? "Reactivating…" : "Reactivate subscription"}
                  </button>
                ) : (
                  !willCancel && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          disabled={!!action}
                          className="w-full border border-black/10 text-[#4A5568] font-semibold py-3.5 rounded-full hover:bg-black/5 transition-colors disabled:opacity-60"
                          data-testid="manage-cancel-btn"
                        >
                          {action === "cancel" ? "Cancelling…" : "Cancel subscription"}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="manage-cancel-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {isStripe
                              ? "You'll keep full access until the end of your current billing period. You can reactivate anytime before then."
                              : "Your PayPal subscription will be cancelled. You can resubscribe anytime."}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="manage-cancel-dismiss">Keep subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={cancel}
                            className="bg-[#2D3748]"
                            data-testid="manage-cancel-confirm"
                          >
                            Yes, cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                )}

                {isPaypal && (
                  <p className="text-xs text-center text-muted-foreground">
                    Payment methods for PayPal are managed inside your PayPal account.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-3xl p-7 shadow-sm border border-black/5 mt-6" data-testid="manage-billing-history">
              <h3 className="font-head font-bold text-lg">Billing history</h3>
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3" data-testid="manage-history-empty">
                  {isStripe
                    ? "No invoices yet. Your first invoice will appear here after your next payment."
                    : "Billing history for PayPal is available in your PayPal account."}
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-black/5">
                  {history.map((inv) => (
                    <li key={inv.id} className="flex items-center justify-between py-3" data-testid={`manage-invoice-${inv.id}`}>
                      <div>
                        <p className="font-semibold text-sm">{fmtDate(inv.date)}</p>
                        <p className="text-xs text-muted-foreground capitalize">{inv.status}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-sm">
                          ${inv.amount.toFixed(2)} {inv.currency}
                        </span>
                        {inv.invoice_url && (
                          <a
                            href={inv.invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2D3748] hover:underline flex items-center gap-1 text-sm font-semibold"
                            data-testid={`manage-invoice-link-${inv.id}`}
                          >
                            View <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
